import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, CheckCircle, AlertCircle } from 'lucide-react';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(''); // 'loading', 'success', 'error'
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setMessage('');

    // Email validation before sending
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }

    try {
      // Intelligent API URL detection
      const isLocalhost = window.location.hostname === 'localhost';
      const isPlatformDomain = window.location.hostname === 'platform.glowytics.com';
      
      let apiUrl;
      if (isLocalhost) {
        apiUrl = 'http://localhost:5000';
      } else if (isPlatformDomain) {
        apiUrl = 'https://api.glowytics.com';
      } else {
        // Fallback for other domains
        apiUrl = process.env.NODE_ENV === 'production' 
          ? 'https://api.glowytics.com' 
          : 'http://localhost:5000';
      }
      
      const response = await fetch(`${apiUrl}/api/password-reset/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to send reset email');
      }

      setStatus('success');
      setMessage(data.message || 'Password reset email sent! Please check your inbox and follow the instructions.');
    } catch (error) {
      console.error('Forgot password error:', error);
      setStatus('error');
      
      // Better error messages
      if (error.message.includes('fetch')) {
        setMessage('Unable to connect to server. Please check your internet connection and try again.');
      } else if (error.message.includes('rate limit')) {
        setMessage('Too many requests. Please wait a few minutes before trying again.');
      } else {
        setMessage(error.message || 'An error occurred. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Left Column: Form */}
      <div className="w-full lg:w-[45%] xl:w-[40%] flex flex-col justify-center items-center p-8 sm:p-12 bg-white shadow-xl z-10">
        <div className="w-full max-w-sm">
          {/* Logo */}
          <img
            src="/Calisma-Yuzeyi-20@5x-4.png"
            alt="Glowytics Logo"
            className="h-9 mb-10"
          />

          {/* Back to Login Link */}
          <Link
            to="/login"
            className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-500 mb-8 transition"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Login
          </Link>

          {/* Header */}
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            Reset Your Password
          </h1>
          <p className="text-sm text-gray-500 mb-8">
            Enter your email address and we'll send you a link to reset your password.
          </p>

          {/* Status Messages */}
          {status === 'success' && (
            <div className="mb-6 rounded-md bg-green-50 p-4 border border-green-200">
              <div className="flex">
                <CheckCircle className="h-5 w-5 text-green-400 mr-3 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-green-800">Email Sent!</h3>
                  <p className="text-xs text-green-700 mt-1">{message}</p>
                </div>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="mb-6 rounded-md bg-red-50 p-4 border border-red-200">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400 mr-3 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <p className="text-xs text-red-700 mt-1">{message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Reset Form */}
          {status !== 'success' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-xs font-medium text-gray-600 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition sm:text-sm"
                    placeholder="you@company.com"
                    disabled={status === 'loading'}
                    pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}"
                    title="Please enter a valid email address"
                    autoComplete="email"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === 'loading' ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4" />
                    Send Reset Email
                  </>
                )}
              </button>
            </form>
          )}

          {/* Success Actions */}
          {status === 'success' && (
            <div className="space-y-4">
              <Link
                to="/login"
                className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Login
              </Link>
              
              <button
                onClick={() => {
                  setStatus('');
                  setMessage('');
                  setEmail('');
                }}
                className="w-full text-center text-sm text-indigo-600 hover:text-indigo-500 transition"
              >
                Try another email address
              </button>
            </div>
          )}

          {/* Help Text */}
          <div className="mt-8 text-center">
            <p className="text-xs text-gray-500">
              Didn't receive the email? Check your spam folder or{' '}
              <a href="mailto:support@glowytics.com" className="text-indigo-600 hover:text-indigo-500">
                contact support
              </a>
            </p>
          </div>

          {/* Privacy Notice */}
          <div className="mt-6 p-3 bg-gray-50 rounded-md">
            <p className="text-xs text-gray-600 leading-relaxed">
              <strong>Privacy Notice:</strong> We'll only use your email to send password reset instructions. 
              For more information, see our{' '}
              <a href="/privacy-policy" className="text-indigo-600 hover:text-indigo-500 underline">
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </div>
      </div>

      {/* Right Column: Visual (Same as Login) */}
      <div className="hidden lg:flex lg:w-[55%] xl:w-[60%] bg-gradient-to-br from-[#0D122B] via-[#101C48] to-[#1F2A68] relative items-center justify-center p-10 xl:p-16 text-white overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 z-0 opacity-40 mix-blend-soft-light">
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
          <div className="absolute w-[700px] h-[700px] bg-gradient-radial from-blue-600/30 via-transparent to-transparent rounded-full -top-60 -left-60 animate-pulse-slow filter blur-3xl"></div>
          <div className="absolute w-[600px] h-[600px] bg-gradient-radial from-violet-700/20 via-transparent to-transparent rounded-full -bottom-60 -right-60 animate-pulse-slow animation-delay-2000 filter blur-3xl"></div>
        </div>

        {/* Content Overlay */}
        <div className="relative z-10 flex flex-col items-center text-center max-w-xl">
          {/* Lock Icon for Security */}
          <div className="w-28 h-28 mb-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
            <Mail className="w-12 h-12 text-blue-300/80" strokeWidth={1} />
          </div>

          <h2 className="text-3xl xl:text-4xl font-bold mb-4 leading-tight">
            Secure Password Recovery
          </h2>
          <p className="text-base xl:text-lg text-indigo-100/70 mb-10">
            We'll send you a secure link to reset your password and get you back to optimizing your marketing campaigns.
          </p>

          {/* Security Features */}
          <div className="flex justify-center space-x-8 text-indigo-200/70">
            <div className="flex flex-col items-center" title="Secure Process">
              <CheckCircle className="h-6 w-6 mb-1" strokeWidth={1.5}/>
              <span className="text-xs font-light">Secure</span>
            </div>
            <div className="flex flex-col items-center" title="Quick Recovery">
              <Mail className="h-6 w-6 mb-1" strokeWidth={1.5}/>
              <span className="text-xs font-light">Fast</span>
            </div>
            <div className="flex flex-col items-center" title="Protected Data">
              <AlertCircle className="h-6 w-6 mb-1" strokeWidth={1.5}/>
              <span className="text-xs font-light">Protected</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 